'use client'

import { useTranslations } from 'next-intl'
import type { MouseEvent } from 'react'
import AppLink from '@/components/elementary/AppLink'
import { ButtonGroup } from '@/components/ui/button-group'
import { APP_LOCALES, LOCALE_TRANSLATION } from '@/constants/i18n.constants'
import { cn } from '@/lib/styles'
import type { AppLocale } from '@/types/general'
import type { LocaleLink } from '@/types/i18n.types'

type Props = {
	currentLocale: AppLocale
	localeLinks: LocaleLink[]
	onLocaleChange?: (locale: AppLocale) => void
}

export function MobileLocaleSwitcher({ currentLocale, localeLinks, onLocaleChange }: Props) {
	const t = useTranslations('LocaleSwitcher')
	// Controlled mode: when a parent manages locale state, prevent navigation and delegate the update.
	const handleLocaleLinkClick = (locale: AppLocale) => (event: MouseEvent<HTMLAnchorElement>) => {
		if (!onLocaleChange) return
		event.preventDefault()
		onLocaleChange(locale)
	}

	// Fixed display order based on APP_LOCALES (en, es, fr, de)
	const sortedLinks = APP_LOCALES.reduce<LocaleLink[]>((acc, locale) => {
		const link = localeLinks.find((link) => link.locale === locale)
		if (link) acc.push(link)
		return acc
	}, [])

	return (
		<ButtonGroup aria-label={t('selectLanguage')} className="w-full">
			{sortedLinks.map((link) => {
				const isActive = link.locale === currentLocale

				const sharedClassNames =
					'flex-1 inline-flex items-center justify-center rounded-full border border-neutral-500 h-12 text-neutral-900 uppercase'

				return isActive ? (
					<span
						key={link.locale}
						aria-current="true"
						className={cn('lok-text-body-bold-large bg-peach-200', sharedClassNames)}
					>
						{link.locale}
					</span>
				) : (
					<AppLink
						key={link.locale}
						href={link.pathname}
						locale={link.locale}
						variant="none"
						size="none"
						aria-label={LOCALE_TRANSLATION[link.locale as keyof typeof LOCALE_TRANSLATION]}
						className={cn('lok-text-body-regular-large', sharedClassNames)}
						onClick={handleLocaleLinkClick(link.locale)}
					>
						{link.locale}
					</AppLink>
				)
			})}
		</ButtonGroup>
	)
}
